import { parse } from 'search-query-parser';
import { GraphQLQuery } from '@lambda-layer/types/filter.ts';
import log from 'loglevel';

/**
 * Allow quotes through so the parser can see them.
 */
export const sanitizeSearch = (input: string): string =>
  input.replace(/[^a-zA-Z0-9:\- ,"]/g, '').trim();

// Helper: split a token into lowercase alphanumeric subtokens, preserving order and de-duping.
const splitIntoSubtokens = (s: string): string[] => {
  const parts = s.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    if (!seen.has(p)) {
      out.push(p);
      seen.add(p);
    }
  }
  return out;
};

type ParsedResult =
  | string[]
  | { text?: string[]; [keyword: string]: string | string[] | undefined };

/**
 * Constructs a GraphQL filter query from a user-entered search string.
 *
 * 1. If the user types "firstName: Sam", "lastName: Smith", or "address: Walnut",
 *   we directly create a wildcard filter on those fields.
 * 2. Otherwise, we split the entire string on commas/spaces, producing tokens,
 *    and for each token create an OR across all fields, then AND them together.
 *
 * Example for "lastname, firstname":
 * {
 *   and: [
 *     {
 *       or: [
 *         { firstName: { wildcard: "*lastname*" } },
 *         { lastName: { wildcard: "*lastname*" } },
 *         { address: { wildcard: "*lastname*" } }
 *       ]
 *     },
 *     {
 *       or: [
 *         { firstName: { wildcard: "*firstname*" } },
 *         { lastName: { wildcard: "*firstname*" } },
 *         { address: { wildcard: "*firstname*" } }
 *       ]
 *     }
 *   ]
 * }
 */
export function buildSearchGraphQLQuery<T>(
  searchString: string,
  searchFields: (keyof T)[]
): GraphQLQuery<T> {
  log.debug(`Received search string: ${searchString}`);

  // Parse with tokenize:true so quoted phrases stay intact
  const parsed = parse(searchString, {
    keywords: searchFields as string[],
    tokenize: true,
    offsets: false,
  }) as ParsedResult;
  log.debug(`Parsed object: ${JSON.stringify(parsed)}`);

  // Explicit field:value filters remain unchanged…
  const fieldFilters: GraphQLQuery<T>[] = [];
  if (!Array.isArray(parsed)) {
    for (const field of searchFields) {
      const rawVal = parsed[field as string];
      if (!rawVal) continue;
      const vals = Array.isArray(rawVal) ? rawVal : [rawVal];
      for (const v of vals) {
        const text = String(v);
        if (text.includes(' ')) {
          // phrase query for spaces
          fieldFilters.push({
            [field]: { matchPhrase: text },
          } as GraphQLQuery<T>);
        } else {
          // punctuation-aware split for single-token values like "R-0"
          const subs = splitIntoSubtokens(text);
          if (subs.length === 0) continue;
          if (subs.length === 1) {
            fieldFilters.push({
              [field]: { wildcard: `*${subs[0]}*` },
            } as GraphQLQuery<T>);
          } else {
            fieldFilters.push({
              and: subs.map(st => ({
                [field]: { wildcard: `*${st}*` },
              })) as unknown as GraphQLQuery<T>[],
            } as GraphQLQuery<T>);
          }
        }
      }
    }
  }
  if (fieldFilters.length > 0) {
    return fieldFilters.length === 1
      ? fieldFilters[0]
      : ({ and: fieldFilters } as GraphQLQuery<T>);
  }

  // FREE‐TEXT: extract raw tokens (quotes intact)
  let rawTokens: string[];
  if (Array.isArray(parsed)) {
    rawTokens = parsed;
  } else if (parsed.text && Array.isArray(parsed.text)) {
    rawTokens = parsed.text;
  } else {
    const re = /"([^"]+)"|(\S+)/g;
    rawTokens = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(searchString)) !== null) {
      rawTokens.push(m[1] ?? m[2]);
    }
  }
  log.debug(`Raw tokens: ${JSON.stringify(rawTokens)}`);

  // QUICK SEARCH: single-character token appears?
  const idxSingle = rawTokens.findIndex(tok => tok.length === 1);
  if (idxSingle >= 0) {
    // phrase group is the single-char token + the preceding token (or itself if idxSingle=0)
    const start = idxSingle > 0 ? idxSingle - 1 : 0;
    const phraseTokens = rawTokens.slice(start, idxSingle + 1);
    const phrase = phraseTokens.join(' ');

    // wildcard tokens are everything outside that phrase group
    const wildcards = [
      ...rawTokens.slice(0, start),
      ...rawTokens.slice(idxSingle + 1),
    ];

    const clauses: GraphQLQuery<T>[] = [];

    // wildcard for each outside token (split on punctuation)
    for (const w of wildcards) {
      const subs = splitIntoSubtokens(w);
      for (const st of subs) {
        clauses.push({
          or: searchFields.map(field => ({
            [field]: { wildcard: `*${st}*` },
          })),
        } as GraphQLQuery<T>);
      }
    }

    // one phrase‐match on the two‐token group
    clauses.push({
      or: searchFields.map(field => ({
        [field]: { matchPhrase: phrase },
      })),
    } as GraphQLQuery<T>);

    const combined = { and: clauses } as GraphQLQuery<T>;
    log.debug(`Built mixed wildcard+phrase: ${JSON.stringify(combined)}`);
    return combined;
  }

  // SINGLE quoted‐phrase still a pure OR of matchPhrase
  if (rawTokens.length === 1 && rawTokens[0].includes(' ')) {
    const phrase = rawTokens[0];
    const orClauses = searchFields.map(field => ({
      [field]: { matchPhrase: phrase },
    })) as GraphQLQuery<T>[];
    return { or: orClauses } as GraphQLQuery<T>;
  }

  // FALLBACK: one-word wildcards across all tokens (split on punctuation)
  const tokens = rawTokens.flatMap(t => splitIntoSubtokens(t)).filter(Boolean);
  if (tokens.length === 0) {
    return {};
  }
  const andClauses = tokens.map(token => ({
    or: searchFields.map(field => ({
      [field]: { wildcard: `*${token}*` },
    })),
  })) as GraphQLQuery<T>[];

  const wildcardQuery = { and: andClauses } as GraphQLQuery<T>;
  log.debug(`Built wildcard AND/OR query: ${JSON.stringify(wildcardQuery)}`);
  return wildcardQuery;
}
